import filter from 'lodash/filter'
import first from 'lodash/first'
import * as React from 'react'
import {Dialog, DialogDismiss} from '@ariakit/react'
import * as dialogStyles from '@frontrow/console/src/components/dialog/dialog.module.scss'
import {Button} from '@frontrow/console/src/components/button'
import {H3} from '@frontrow/console/src/components/typography'
import {exhaustive} from '@freckle/exhaustive'
import {maybe, fromMaybe, mthen} from '@freckle/maybe'
import {
  GenericSelector,
  createSelectorOptionAsItem
} from '@frontrow/console/src/components/generic-selector'
import {Text} from '@frontrow/console/src/components/typography'
import {isSchoolCrmSynced} from '@frontrow/console/src/entities/models/school'

import {
  type DetailedSchoolT,
  type SchoolDeletionInfoT,
  type SchoolAttrs
} from '@frontrow/console/src/models/school'
import * as style from './index.module.scss'

type Props = {
  show: boolean
  hideModal: () => void
  onSubmit: (state: State) => Promise<void>
  schoolToDelete: DetailedSchoolT
  schoolDeletionInfo: SchoolDeletionInfoT
  schoolsInDistrict: Array<SchoolAttrs> | undefined | null
}

type CannotDeleteSchoolReasonT = 'active-crm' | 'cannot-merge'
export type State =
  | {
      tag: 'safe-delete'
      schoolName: string
    }
  | {
      tag: 'needs-merge'
      schoolDeletionInfo: SchoolDeletionInfoT
      schoolsInDistrict: Array<SchoolAttrs>
      selectedTargetSchoolId: number
    }
  | {
      tag: 'cannot-delete'
      reason: CannotDeleteSchoolReasonT
    }

function deriveState(
  schoolToDelete: DetailedSchoolT,
  schoolDeletionInfo: SchoolDeletionInfoT,
  schoolsInDistrict?: Array<SchoolAttrs> | null
): State {
  const {id, name} = schoolToDelete

  const crmSynced = isSchoolCrmSynced(schoolToDelete)

  const {teacherCount, schoolAdminCount, courseCount} = schoolDeletionInfo
  if (crmSynced) {
    return {tag: 'cannot-delete', reason: 'active-crm'}
  } else if (teacherCount === 0 && schoolAdminCount === 0 && courseCount === 0) {
    return {tag: 'safe-delete', schoolName: name}
  } else {
    const filteredSchoolsInDistrict = filter(
      fromMaybe(() => [], schoolsInDistrict),
      school => school.id !== id // Cannot merge a school to itself
    )
    return maybe(
      () => ({tag: 'cannot-delete', reason: 'cannot-merge'}),
      selectedTargetSchoolId => ({
        // @ts-ignore Potetial error with the code
        tag: 'needs-merge',
        schoolDeletionInfo,
        schoolsInDistrict: filteredSchoolsInDistrict,
        selectedTargetSchoolId
      }),
      mthen(first(filteredSchoolsInDistrict), ({id}) => id)
    )
  }
}

function reduceSelectedTargetSchoolId(prevState: State, selectedTargetSchoolId: number): State {
  if (prevState.tag === 'needs-merge') {
    return {
      ...prevState,
      selectedTargetSchoolId
    }
  } else {
    return prevState
  }
}

const ModalTitle = ({state, schoolName}: {state: State; schoolName: string}) => {
  switch (state.tag) {
    case 'safe-delete':
      return <H3>Delete {schoolName}?</H3>
    case 'needs-merge':
      return <H3>Delete {schoolName}?</H3>
    case 'cannot-delete':
      return <H3>Cannot delete {schoolName}</H3>
    default:
      return exhaustive(state as never)
  }
}

const ModalContent = ({
  state,
  onChange
}: {
  state: State
  onChange: (state: State, id: number) => void
}) => {
  switch (state.tag) {
    case 'safe-delete':
      return (
        <>
          <Text style="body-2">
            There are no teachers, courses or school admins associated with this school. Deleting
            the school will remove it from Freckle Console. This school will no longer appear as an
            option for teachers to select when onboarding or within their settings.
          </Text>
          <Text style="body-2">
            <strong>Are you sure you want to delete {state.schoolName}?</strong>
          </Text>
        </>
      )
    case 'needs-merge':
      const {schoolDeletionInfo, schoolsInDistrict, selectedTargetSchoolId} = state
      const {teacherCount, schoolAdminCount, courseCount} = schoolDeletionInfo
      const schoolOptions = schoolsInDistrict.map(({id, name}) =>
        createSelectorOptionAsItem(id, name)
      )
      return (
        <>
          <Text style="body-2">
            This school has ({teacherCount}) teachers, ({courseCount}) courses and (
            {schoolAdminCount}) school admins associated with it. To delete this school, you must
            move existing school admins to another school, and either move teachers to a different
            school or delete them. Teachers will not lose any of their students or practice data.
          </Text>
          <Text style="body-2">
            If you want to move all teachers, courses and school admins to a school that you can't
            find in this dropdown, you must first update this school's district ID to match the
            district ID of the target school.
          </Text>
          <Text style="subtitle-2">Where do you want to move teachers, courses and admins?</Text>
          <GenericSelector
            onChangeSelection={id => onChange(state, id)}
            selection={selectedTargetSchoolId}
            collection={schoolOptions}
          />
        </>
      )
    case 'cannot-delete':
      switch (state.reason) {
        case 'active-crm':
          return (
            <Text style="body-2">
              This school cannot be deleted because it has a CRM ID and is marked active. If you
              want to remove this school, you must first mark this school as inactive in CRM.
            </Text>
          )
        case 'cannot-merge':
          return (
            <Text style="body-2">
              This school cannot be deleted because it has teachers or school admins associated with
              it. There are no other schools in the district with which to merge these entities to.
              If you want to move all teachers and school admins to a new school, you must first
              update this school's district ID to match the district ID of the target school.
            </Text>
          )
        default:
          return exhaustive(state.reason as never)
      }
    default:
      return exhaustive(state as never)
  }
}

const ModalButtons = ({state, hideModal}: {state: State; hideModal: () => void}) => {
  switch (state.tag) {
    case 'safe-delete':
      return (
        <>
          <Button style="secondary" addClass={style.schoolFormCancel} onClick={hideModal}>
            Cancel
          </Button>
          <Button style="danger" type="submit">
            Delete School
          </Button>
        </>
      )
    case 'needs-merge':
      return (
        <>
          <Button style="secondary" addClass={style.schoolFormCancel} onClick={hideModal}>
            Cancel
          </Button>
          <Button type="submit">Move Users & Delete School</Button>
        </>
      )
    case 'cannot-delete':
      switch (state.reason) {
        case 'active-crm':
          return <Button onClick={hideModal}>Got It</Button>
        case 'cannot-merge':
          return <Button onClick={hideModal}>Ok</Button>
        default:
          return exhaustive(state.reason as never)
      }
    default:
      return exhaustive(state as never)
  }
}

export function SchoolsDeleteModal(props: Props): React.ReactElement {
  const {
    show,
    hideModal,
    schoolToDelete,
    schoolsInDistrict,
    onSubmit: baseSubmit,
    schoolDeletionInfo
  } = props

  const {name: schoolName} = schoolToDelete

  const [state, setState] = React.useState(
    deriveState(schoolToDelete, schoolDeletionInfo, schoolsInDistrict)
  )

  React.useEffect(() => {
    const updatedState = deriveState(schoolToDelete, schoolDeletionInfo, schoolsInDistrict)
    setState(updatedState)
  }, [schoolToDelete, schoolDeletionInfo, schoolsInDistrict])

  const onSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    baseSubmit(state)
  }

  const updatedSelectedTargetSchoolId = (prevState: State, schoolId: number) => {
    const updatedState = reduceSelectedTargetSchoolId(prevState, schoolId)
    setState(updatedState)
  }

  return (
    <Dialog
      open={show}
      onClose={hideModal}
      backdrop={<div className={dialogStyles.backdrop} />}
      className={dialogStyles.dialog}
      portal
      unmountOnHide
      hideOnEscape
      hideOnInteractOutside={false}
    >
      <div className={dialogStyles.header}>
        <div className={dialogStyles.title}>
          <ModalTitle state={state} schoolName={schoolName} />
        </div>
        <DialogDismiss className={dialogStyles.closeButton}>
          <span aria-hidden="true">&times;</span>
        </DialogDismiss>
      </div>

      <div className={dialogStyles.body}>
        <form onSubmit={onSubmit} action="">
          <div className={style.schoolFormContainer}>
            <div className={style.schoolAreaContainer}>
              <ModalContent state={state} onChange={updatedSelectedTargetSchoolId} />
            </div>
          </div>

          <div className={style.schoolFormButtonContainer}>
            <ModalButtons state={state} hideModal={hideModal} />
          </div>
        </form>
      </div>
    </Dialog>
  )
}
